import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { useLocation } from '../../contexts/LocationContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useSearchState, DEFAULT_FILTERS } from '../../contexts/SearchStateContext';
import locationService from '../../services/locationService';
import { resolveExpectedStateCode, isLocationInExpectedState, STATE_ABBREVIATION_TO_NAME } from '../../utils/locationStateValidation';
import './styles.css';
import SearchBar from '../../components/searchBar/searchBar';
import SearchLocation from '../../components/searchLocation/searchLocation';
import PopularSearch from '../../components/popularSearch/popularSearch';
import BrowseBy from '../../components/browseBy/browseBy';
import StateOptions from '../../components/stateOptions/stateOptions';
import PlanSelectionModal from '../../components/planSelectionModal/planSelectionModal';
import CSEDProviderMap, { CSED_QUERY, COUNTY_COORDINATES } from '../../components/csedMap/CSEDProviderMap';
import ApiErrorBanner from '../../components/errorMessages/ApiErrorBanner';
import { ERROR_TYPES } from '../../components/errorMessages/errorMessages.constants';
import arrowRight from '../../images/arrowRight.svg';


const Search = ({ envPlanCode = '' } = {}) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { planFeatures, setPlanCode, isSectionEnabled, getCentralZip, getFooterLinks, getStateName } = usePlanFeatures();
    const { translationError } = useTranslation();
    const { userLocation, updateUserLocation } = useLocation();
    // Phase 3 (URL Parameter Removal): SearchStateContext is the shared, cross-page store that
    // Results.jsx also reads from. Pushing into it here (in the 3 nav handlers below) is how
    // query/filters/location/plan flow to the Results page without ever touching the URL.
    const { searchState, updateSearchState } = useSearchState();

    // Resolve the environment plan code (e.g. "FL"): prefer the path-sourced prop passed
    // down from FindCareSearch in App.jsx (Phase 0 — URL Parameter Removal). Fall back to
    // the legacy `?plan=` query param for the bare /search route (guarded by RequiresPlan),
    // which has no :planCode path segment to read from.
    const resolvedEnvPlan = envPlanCode || searchParams.get('plan') || '';

    // The cached location from LocationContext, but ONLY trusted if it was actually resolved
    // for the state/plan loaded right now (resolvedEnvPlan) — otherwise treated as if there
    // were no cached location at all. There's no in-app UI to switch states, so "changing
    // states" always means loading a different /find-care/:planCode URL in the same browser
    // tab/session; LocationContext's userLocation is lazy-restored from sessionStorage on that
    // fresh mount, so without this guard a previous state's address (and worse, its
    // coordinates — see handleSearch/handlePopularSearch/handleBrowseBySearch below) would
    // silently carry over and even be searched against. Every effect/handler below that
    // decides whether to reuse vs. re-detect location reads THIS instead of the raw context
    // value. processLocation/useCentralZipFallback below already tag every location they
    // resolve with `planCode: resolvedEnvPlan` specifically so this comparison works,
    // including on the very next page load. Untagged (legacy) cached values are also treated
    // as a mismatch — safe, costs at most one redundant detection.
    const currentStateLocation = (userLocation && userLocation.planCode === resolvedEnvPlan) ? userLocation : null;

    // selectedPlan: prefer a fresh deep-link `?planCode=` query param (explicit intent from a
    // shared/bookmarked link), otherwise restore whatever plan was selected earlier this
    // session from SearchStateContext — e.g. the user went Search -> Results -> back to
    // Search (browser back, or the breadcrumb) and shouldn't have to re-pick their plan.
    // Safe: searchState.selectedPlan is only ever written (see handleSearch/handlePopularSearch/
    // handleBrowseBySearch below) AFTER validatePlanSelection() has confirmed it's a real,
    // valid plan code — it can never be the raw env identifier. Read once via a lazy useState
    // initializer (not an effect) since both sources are synchronously available at mount.
    // NOTE: this continuity behavior isn't in the plan's Phase 3.1-3.4 samples (which only
    // show selectedPlan as pre-existing local state) — added while implementing Phase 3 since
    // the infrastructure (context + sessionStorage persistence) already makes it free.
    const [selectedPlan, setSelectedPlan] = useState(() => searchParams.get('planCode') || searchState.selectedPlan || '');
    const [showPlanError, setShowPlanError] = useState(false);
    // Toggled by the "csedProv" link inside StateOptions' CMS-authored content (WV's
    // "Children with Serious Emotional Disorder Waiver" county map). While true, the
    // rest of this page's own content (title, search location/bar, popular search,
    // browse by, state options, legal notices) is hidden and replaced with the CSED
    // county map instead — the global Header/Footer (rendered by App.jsx, above/below
    // this whole page) are unaffected and stay visible either way.
    const [showCsedProviderMap, setShowCsedProviderMap] = useState(false);
    const handleCsedProviderClick = () => setShowCsedProviderMap(true);
    // Mirrors handleSearch/handlePopularSearch/handleBrowseBySearch below: pushes the CSED
    // query + the selected county's approximate lat/long into SearchStateContext, then
    // navigates to the Results page for this plan, exactly like every other search entry
    // point on this page. Results.jsx already knows how to run a plain `query` search with
    // explicit `latitude`/`longitude` (see its searchQuery/fetchResults handling), so no
    // Results.jsx changes are needed — the CSED query string just flows through as a normal
    // free-text query, using the county centroid instead of the user's own location.
    const handleCsedFindProvider = (county) => {
        const coords = COUNTY_COORDINATES[county];
        if (!coords) {
            console.warn(`CSED provider search: no coordinates found for county "${county}"`);
            return;
        }

        const newState = {
            query: CSED_QUERY,
            superCategory: '',
            subCategory: '',
            popularSearchId: '',
            psLabel: '',
            selectedPlan,
            latitude: coords.lat,
            longitude: coords.long,
            filters: { ...DEFAULT_FILTERS, distance },
            currentPage: 1
        };
        updateSearchState(newState);

        navigate(`/find-care/${resolvedEnvPlan || 'FL'}/results`, {
            state: { searchStateSnapshot: { ...searchState, ...newState } }
        });
    };
    // Mandatory "choose your plan" modal — pops open automatically as soon as plan features
    // load and reveal more than one selectable plan/line of business (planFeatures.planCode has
    // more than one entry) with nothing valid chosen yet. See the planFeatures-driven effect
    // below for exactly when this flips true/false. Deliberately has no cancel/close affordance
    // (see planSelectionModal.jsx) — same "must pick before proceeding" rule the inline
    // .plan-selector-container dropdown (planSelector.jsx, rendered inside SearchLocation below)
    // already enforced reactively via validatePlanSelection() at search-submit time; this just
    // surfaces that requirement immediately instead of waiting for the user to hit Search and
    // get bounced back with an inline error. The dropdown is left in place after this closes so
    // the user can still change their mind before actually searching.
    const [showPlanSelectionModal, setShowPlanSelectionModal] = useState(false);
    // distance: same continuity treatment as selectedPlan — seeded from context's persisted
    // filters so a previously-chosen search radius carries back over too, instead of always
    // resetting to the 25-mile default.
    const [distance, setDistance] = useState(() => searchState.filters?.distance || '25');
    // One-time capture of a hand-built `?query=` deep link (e.g. an external link meant to
    // pre-fill the search box). Read once via a lazy initializer — same reasoning as
    // selectedPlan/distance above — rather than a live `searchParams.get('query')` read in
    // JSX, so it keeps working unchanged once the mount effect below cleans the URL.
    const [initialQuery] = useState(() => searchParams.get('query') || '');
    const [locationAddress, setLocationAddress] = useState('');
    const [locationFallbackNeeded, setLocationFallbackNeeded] = useState(false);
    const locationFallbackExecutedRef = useRef(false);
    // Bumped (never reset) whenever browser geolocation resolves outside the plan's state, or
    // fails to produce a usable address, AND the central-zip fallback also can't produce one —
    // see processLocation/useCentralZipFallback below. SearchLocation/LocationPanel watch this
    // to auto-open the location panel with autoOpenLocationPanelError already populated,
    // instead of silently leaving the user on a generic/blank fallback with no explanation.
    const [autoOpenLocationPanelSignal, setAutoOpenLocationPanelSignal] = useState(0);
    const [autoOpenLocationPanelError, setAutoOpenLocationPanelError] = useState('');

    // Call setPlanCode IMMEDIATELY on component creation to prevent the 500ms fallback timer
    // from firing before the URL plan code is processed. Do NOT set selectedPlan here —
    // the useEffect below handles that, and the validation effect will clear it if it turns
    // out to be an env identifier rather than a real plan code.
    if (resolvedEnvPlan && !selectedPlan) {
        setPlanCode(resolvedEnvPlan);
    }

    // Set a descriptive, page-specific document title (was previously the static "Find Care"
    // from index.html for every route, which is indistinguishable from the Results page's
    // title to screen reader users and browser tab/history/bookmark lists). Swaps to the
    // CSED-specific title while the CSED provider map view is showing (see
    // showCsedProviderMap/handleCsedProviderClick above), mirroring the <h1> swap below.
    useEffect(() => {
        document.title = showCsedProviderMap
            ? 'Find a CSED provider | Aetna Better Health'
            : 'Find Care | Aetna Better Health';
    }, [showCsedProviderMap]);

    // Load Google Maps script early for geocoding
    useEffect(() => {
        const loadGoogleMaps = () => {
            // Already loaded
            if (window.google?.maps?.places) {
                return Promise.resolve();
            }

            // Script already in document, wait for it
            if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                return new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => reject(new Error('Google Maps load timeout')), 15000);
                    const interval = setInterval(() => {
                        if (window.google?.maps?.places) {
                            clearInterval(interval);
                            clearTimeout(timeout);
                            resolve();
                        }
                    }, 150);
                });
            }

            // Load script
            const apiKey = window.APP_CONFIG?.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
            if (!apiKey) {
                console.error('❌ Google Maps API key not found');
                return Promise.reject(new Error('No API key'));
            }

            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places&loading=async`;
                script.async = true;
                script.defer = true;
                
                const timeout = setTimeout(() => reject(new Error('Script load timeout')), 15000);
                
                script.onload = () => {
                    const interval = setInterval(() => {
                        if (window.google?.maps?.places) {
                            clearInterval(interval);
                            clearTimeout(timeout);
                            resolve();
                        }
                    }, 150);
                };
                
                script.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('Script load failed'));
                };
                
                document.head.appendChild(script);
            });
        };

        loadGoogleMaps().catch(err => {
            console.error('❌ Failed to load Google Maps:', err);
        });
    }, []); // Run once on mount

    // --- Phase 3 (URL Parameter Removal): one-time plan-sync + URL clean-up, runs once on mount ---
    // Combines two things that used to be split across a reactive effect + a live URL read in
    // JSX: (1) syncing resolvedEnvPlan into PlanFeaturesContext and defaulting selectedPlan to
    // it when nothing better is available, and (2) stripping any inbound query params (the
    // legacy `?plan=` from the RequiresPlan-guarded bare /search route or old bookmarks, or a
    // hand-built `?planCode=`/`?query=` deep link) down to a clean path.
    //
    // This MUST be mount-only (ref-guarded, empty deps) rather than a `[resolvedEnvPlan,
    // searchParams, setPlanCode]`-keyed reactive effect like before: the URL clean-up navigate()
    // below changes `searchParams`'s identity, which would otherwise re-trigger this effect and
    // re-run `setSelectedPlan(resolvedEnvPlan)`, clobbering a real selected plan (restored from
    // the `?planCode=` deep link or SearchStateContext continuity — both already captured once,
    // above, by the lazy useState initializer) back to the raw env identifier. Mirrors the same
    // mount-only pattern results.jsx's Phase 2.2 hydration effect uses for the same reason.
    const initialLoadRef = useRef(false);
    useEffect(() => {
        if (initialLoadRef.current) return;
        initialLoadRef.current = true;

        if (resolvedEnvPlan) {
            setPlanCode(resolvedEnvPlan);
            // Only fall back to the raw env identifier if nothing better already populated
            // selectedPlan (deep-link `?planCode=` or restored session context — see the lazy
            // initializer above). The validation effect further below clears this back out if
            // it turns out not to be a real, selectable plan code.
            setSelectedPlan(prev => prev || resolvedEnvPlan);
        }

        // Canonicalize the URL. Safe because selectedPlan and the search box's initial query
        // (see initialQuery below) have already been captured into local state above — nothing
        // on this page reads searchParams again after this point.
        if (resolvedEnvPlan && Array.from(searchParams.keys()).length > 0) {
            navigate(`/find-care/${resolvedEnvPlan}`, { replace: true });
        }
        // Intentionally empty deps — must run exactly once, on mount, regardless of
        // searchParams/navigate identity churn (ref guards StrictMode's double-invoke).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Once plan features load, validate selectedPlan against the real plan codes, and pop open
    // the mandatory plan-selection modal whenever multiple lines of business exist and nothing
    // valid is selected yet.
    //
    // Combined into one effect (rather than a separate modal-visibility effect) so both checks
    // are always computed from the SAME availablePlanCodes/hasValidSelection snapshot —
    // splitting them across two effects watching the same deps would let a modal-visibility
    // effect run with a stale (not-yet-cleared) `selectedPlan` closure on the same render this
    // effect schedules `setSelectedPlan('')` on, since effects don't see each other's
    // same-commit state updates.
    //
    // `selectedPlan` is intentionally in the dependency array (unlike before) — that's what
    // makes the modal close itself the instant a real selection lands, whether that came from
    // the modal (see handlePlanSelectFromModal below) or the inline dropdown.
    useEffect(() => {
        if (!planFeatures) return;
        const availablePlanCodes = planFeatures?.planCode || [];
        const hasMultiplePlans = availablePlanCodes.length > 1;

        if (hasMultiplePlans && selectedPlan && !availablePlanCodes.includes(selectedPlan)) {
            // Not a real plan code (e.g. the raw env identifier like "FL") — clear it so the
            // check below (and the dropdown) correctly treat this as "nothing chosen yet".
            // This re-triggers the effect (selectedPlan is now a dep), which is when the modal
            // visibility check below actually runs against the cleared value.
            setSelectedPlan('');
            return;
        }

        const hasValidSelection = Boolean(selectedPlan) && availablePlanCodes.includes(selectedPlan);
        setShowPlanSelectionModal(hasMultiplePlans && !hasValidSelection);
    }, [planFeatures, selectedPlan]);

    // Shapes planFeatures' parallel planCode/planType arrays into the {planCode, planName}
    // objects planSelectionModal.jsx expects. planType is the long display name field; planName
    // is accepted as a fallback in case some environment's response uses that key instead —
    // mirrors the identical `planType || planName` fallback already used by planSelector.jsx
    // and results.jsx's getPlanDisplayName(). Guards planTypes with Array.isArray (same as
    // planSelector.jsx does) because planName, unlike planType, is sometimes a single scalar
    // display string rather than a per-plan array (see mockData.js's buildPlanFeatures default)
    // — indexing into a string with `planTypes[index]` would silently return single characters
    // instead of real names, so an unrecognized shape falls back to the plan CODE per option
    // instead, never a mangled fragment of a shared name.
    const availablePlansForModal = useMemo(() => {
        const planCodes = planFeatures?.planCode || [];
        const rawPlanTypes = planFeatures?.planType || planFeatures?.planName;
        const planTypes = Array.isArray(rawPlanTypes) ? rawPlanTypes : [];
        if (!Array.isArray(planCodes)) return [];
        return planCodes.map((code, index) => ({
            planCode: code,
            planName: planTypes[index] || code
        }));
    }, [planFeatures]);

    // Request user's location on page load and reverse geocode
    // Skip if we already have a location in context from a previous visit
    useEffect(() => {
        // If we already have a valid location in context (with real coordinates) FOR THIS
        // STATE, use it — see currentStateLocation's declaration above for why this must be
        // the plan-filtered value, not the raw context value.
        if (currentStateLocation && currentStateLocation.latitude && currentStateLocation.longitude) {
            const address = currentStateLocation.formattedAddress ||
                           (currentStateLocation.city && currentStateLocation.stateCode
                               ? `${currentStateLocation.city}, ${currentStateLocation.stateCode}${currentStateLocation.zipCode ? ' ' + currentStateLocation.zipCode : ''}`
                               : '');
            if (address) {
                setLocationAddress(address);
                return; // Skip geolocation request
            }
        }

        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const coords = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    };
                    await processLocation(coords);
                },
                () => {
                    setLocationFallbackNeeded(true);
                },
                {
                    enableHighAccuracy: false,
                    timeout: 10000,
                    maximumAge: 300000 // Cache for 5 minutes
                }
            );
        } else {
            setLocationFallbackNeeded(true);
        }
    }, []); // Empty dependency array - run ONLY once on mount

    // Run centralZip fallback only after plan features are loaded to get the correct zip
    // BUT ONLY if we don't already have a location from context FOR THIS STATE (see
    // currentStateLocation above — a stale cross-state cached location must NOT block this
    // fallback from running, otherwise a state change would be stuck showing the old state's
    // coordinates forever with no fresh detection ever kicking in).
    useEffect(() => {
        if (locationFallbackNeeded && planFeatures && !locationFallbackExecutedRef.current && !currentStateLocation?.latitude) {
            locationFallbackExecutedRef.current = true;
            useCentralZipFallback();
        }
    }, [locationFallbackNeeded, planFeatures, currentStateLocation]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update location address when context changes (e.g., when returning to page). Reads
    // currentStateLocation (not the raw context value) so a cached location left over from a
    // DIFFERENT state can never flash into locationAddress here either.
    useEffect(() => {
        if (currentStateLocation) {
            // Try formattedAddress first, then construct from parts
            const address = currentStateLocation.formattedAddress || 
                           (currentStateLocation.city && currentStateLocation.stateCode 
                               ? `${currentStateLocation.city}, ${currentStateLocation.stateCode}${currentStateLocation.zipCode ? ' ' + currentStateLocation.zipCode : ''}`
                               : '');
            if (address) {
                setLocationAddress(address);
            }
        }
    }, [currentStateLocation]);

    /**
     * Use central zip from plan features to get fallback location
     */
    const useCentralZipFallback = async () => {
        try {
            const centralZip = getCentralZip();
            if (!centralZip) {
                setLocationAddress('Unknown location');
                const expectedState = resolveExpectedStateCode(selectedPlan || resolvedEnvPlan, planFeatures);
                const expectedStateName = STATE_ABBREVIATION_TO_NAME[expectedState] || expectedState || 'your plan\'s state';
                setAutoOpenLocationPanelError(
                    `We couldn't find a location for you. Be sure to fill in a city, county or ZIP code in ${expectedStateName}.`
                );
                setAutoOpenLocationPanelSignal(prev => prev + 1);
                return;
            }
            const coordsData = await locationService.geocodeGoogle({
                zip: centralZip,
                // Bound this geocode to the plan's expected state (resolved from the
                // URL's :planCode segment) — without it, a bare zip-only query has no
                // location bias/restriction yet at this point in the flow, so it can
                // match a same-numbered ZIP/place in a completely different state,
                // silently resolving the plan's own central zip to the wrong state's
                // coordinates and offsetting every distance/result downstream from it.
                state: resolveExpectedStateCode(selectedPlan || resolvedEnvPlan, planFeatures) || undefined
            });
            const locationData = {
                latitude: coordsData.latitude,
                longitude: coordsData.longitude,
                city: coordsData.city,
                stateCode: coordsData.stateCode,
                zipCode: centralZip,
                formattedAddress: coordsData.formattedAddress,
                planCode: resolvedEnvPlan, // Track which plan this location belongs to
                timestamp: Date.now(),
                source: 'centralZip'
            };
            
            if (updateUserLocation) {
                updateUserLocation(locationData);
            }
            setLocationAddress(coordsData.formattedAddress || `${coordsData.city}, ${coordsData.stateCode} ${centralZip}`);

        } catch (error) {
            console.error('❌ Central zip fallback failed:', error.message);
            setLocationAddress('Unknown location');
            const expectedState = resolveExpectedStateCode(selectedPlan || resolvedEnvPlan, planFeatures);
            const expectedStateName = STATE_ABBREVIATION_TO_NAME[expectedState] || expectedState || 'your plan\'s state';
            setAutoOpenLocationPanelError(
                `We couldn't find a location for you. Be sure to fill in a city, county or ZIP code in ${expectedStateName}.`
            );
            setAutoOpenLocationPanelSignal(prev => prev + 1);
        }
    };

    const processLocation = async (coords) => {
        try {
            const addressData = await locationService.reverseGeocodeMCS(coords.latitude, coords.longitude);

            // reverseGeocodeMCS can "succeed" (resolve, no thrown error) while still failing to
            // parse a usable city/state out of the address string it got back — e.g. the
            // address didn't split into the expected 3 comma-separated parts. When that
            // happens, isLocationInExpectedState() below FAILS OPEN (treats "no stateCode" as
            // acceptable, by design, so it doesn't block genuinely ambiguous cases) — so this
            // case was silently slipping past that guard entirely and going on to display a
            // broken/blank "Unknown location"-style address to the user with no explanation.
            // Catch it explicitly here instead, same treatment as an out-of-state resolution.
            const hasUsableAddress = Boolean(addressData?.city) || Boolean(addressData?.stateCode);
            if (!hasUsableAddress) {
                console.warn('⚠️ Browser geolocation reverse-geocode returned no usable city/state, falling back to central zip:', addressData);
                const expectedState = resolveExpectedStateCode(selectedPlan || resolvedEnvPlan, planFeatures);
                const expectedStateName = STATE_ABBREVIATION_TO_NAME[expectedState] || expectedState || 'your plan\'s state';
                setAutoOpenLocationPanelError(
                    `We couldn't find a location for you. Be sure to fill in a city, county or ZIP code in ${expectedStateName}.`
                );
                setAutoOpenLocationPanelSignal(prev => prev + 1);
                await useCentralZipFallback();
                return;
            }

            // Guard against browser geolocation resolving to a location OUTSIDE the current
            // plan's state (e.g. a PA-plan session where the device/network's actual location
            // — or a VPN/proxy exit point — is in California). Without this check, that
            // out-of-state address would be accepted and persisted (tagged
            // `planCode: resolvedEnvPlan`) same as any legitimate in-state address, and every
            // later staleness check only compares planCode, never the actual state, so it
            // would never get caught. On a mismatch, fall back to the plan's central-zip
            // location instead of trusting the out-of-state coordinates.
            const expectedState = resolveExpectedStateCode(selectedPlan || resolvedEnvPlan, planFeatures);
            if (!isLocationInExpectedState(addressData, expectedState)) {
                console.warn('⚠️ Browser geolocation resolved outside plan state, falling back to central zip:', {
                    resolvedState: addressData?.stateCode,
                    expectedState
                });
                // Surface this to the user rather than silently swapping in the plan's generic
                // central-zip location with no explanation — they may genuinely be searching
                // from outside the plan's state (as reported) and need to manually enter an
                // in-state address instead. Use the full state name from the shared crosswalk
                // (e.g. "Arizona") rather than `planDescription`, which is the plan's
                // BUSINESS/marketing name (e.g. "Mercy Care" for AZ plans), not a state name.
                const planDisplayName = STATE_ABBREVIATION_TO_NAME[expectedState] || expectedState || planFeatures?.planDescription || 'your plan';
                setAutoOpenLocationPanelError(
                    `Your current location appears to be outside ${planDisplayName}'s coverage area. Please search for an address within your plan's state.`
                );
                setAutoOpenLocationPanelSignal(prev => prev + 1);
                await useCentralZipFallback();
                return;
            }

            // Store complete location data
            const locationData = {
                ...coords,
                ...addressData,
                planCode: resolvedEnvPlan, // Track which plan this location belongs to
                timestamp: Date.now()
            };
            // Update context
            if (updateUserLocation) {
                updateUserLocation(locationData);
            }

            const displayAddress = addressData.formattedAddress || `${addressData.city}, ${addressData.stateCode} ${addressData.zipCode}`;
            setLocationAddress(displayAddress);

        } catch (geocodeError) {
            console.error('❌ Reverse geocode failed:', geocodeError.message);
            
            // Use city/state from coords if available
            if (coords.city && coords.stateCode && coords.zipCode) {
                const displayAddress = `${coords.city}, ${coords.stateCode} ${coords.zipCode}`;
                setLocationAddress(displayAddress);
            }
        }
    };

    const validatePlanSelection = () => {
        // Check if plan selector is rendered (only renders when there are multiple plans)
        const planSelector = document.querySelector('.plan-selector-container');

        // If plan selector is not rendered, validation passes (no plan selection needed)
        if (!planSelector) {
            return true;
        }

        // A valid selection must be non-empty AND present in the actual plan codes list.
        // This prevents env identifiers like "FL" from passing validation.
        const availablePlanCodes = planFeatures?.planCode || [];
        const isValidPlan = selectedPlan && (availablePlanCodes.length === 0 || availablePlanCodes.includes(selectedPlan));

        if (!isValidPlan) {
            setShowPlanError(true);
            // Scroll to plan selector to show error message
            setTimeout(() => {
                if (planSelector) {
                    planSelector.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    });
                }
            }, 100); // Small delay to ensure error message is rendered
            return false;
        }
        
        setShowPlanError(false);
        return true;
    };

    const handlePlanChange = (selectedPlan) => {
        setSelectedPlan(selectedPlan);
        setShowPlanError(false); // Clear error when user selects a plan
    };

    // Wired to PlanSelectionModal's onPlanSelect. Reuses handlePlanChange for the actual state
    // update (keeps this in sync with whatever the inline dropdown does), then explicitly closes
    // the modal — the effect above will also flip showPlanSelectionModal false on its own once
    // it re-runs with the new selectedPlan, but closing it here too avoids waiting an extra
    // render tick for that.
    const handlePlanSelectFromModal = (planCode) => {
        handlePlanChange(planCode);
        setShowPlanSelectionModal(false);
    };

    const handleDistanceChange = (newDistance) => {
        setDistance(newDistance);
    };

    // Phase 3 (URL Parameter Removal): all 3 nav handlers below push into SearchStateContext
    // instead of building a query string, then navigate to a bare, clean results URL.
    //
    // Each is a brand-new search, so filters are reset to DEFAULT_FILTERS (with only `distance`
    // carried over from this page's radius selector) rather than spread from
    // `searchState.filters` — the plan document's own sample spread the old filters, but that
    // would leak stale gender/specialty/language/etc. selections from a previous results-page
    // session into what the original app always treated as a clean slate (its query strings
    // never carried those filter params in the first place).
    //
    // The dead `type=popular`/`type=browse` params from the old URLs are intentionally dropped
    // — nothing ever read them; results.jsx's determineSearchType() already disambiguates
    // popular/browse/plain-query searches from popularSearchId/superCategory/query alone.
    //
    // Phase 5 (Browser Navigation Support): each handler also attaches the complete resulting
    // state as `navigate(path, { state: { searchStateSnapshot } })`. This never touches the
    // URL (no query string — the path stays exactly as clean as Phase 0-4 established) — it
    // only rides along on the browser's own session-history entry for this navigation. If the
    // user later browses away and then presses the browser's forward button back onto this
    // exact results entry (e.g. after having also gone back further, past it, to the search
    // page), results.jsx reads this snapshot back out of `useLocation().state` and restores it
    // — see results.jsx's Phase 5 notes for the full mechanism and why it replaced Phase 1's
    // original sessionStorage-based pushToHistory/popFromHistory stack.
    const handleSearch = (query, isSuperCategory = false, subCategoryVal = '') => {
        // Validate plan selection before allowing search
        if (!validatePlanSelection()) {
            return; // Stop search if validation fails
        }

        // Get coordinates from context (app memory) — currentStateLocation, not the raw
        // context value, so a stale cross-state cached location can never be searched against.
        let lat = null;
        let lon = null;
        if (currentStateLocation) {
            lat = currentStateLocation.latitude;
            lon = currentStateLocation.longitude;
        }

        // When the SearchBar's typeahead suggestion came from the typesOfCare/whereToGetCare/
        // healthConditions categories, run it as a super-category search (same mechanism as
        // browseBy.jsx's category drill-downs) instead of a plain free-text query search, so
        // results.jsx's determineSearchType()/fetchResults treat it as `superCategory` rather
        // than `query`.
        const newState = {
            query: isSuperCategory ? '' : query,
            superCategory: isSuperCategory ? query : '',
            subCategory: subCategoryVal || '',
            popularSearchId: '',
            psLabel: '',
            selectedPlan,
            latitude: lat,
            longitude: lon,
            filters: { ...DEFAULT_FILTERS, distance },
            currentPage: 1
        };
        updateSearchState(newState);

        navigate(`/find-care/${resolvedEnvPlan || 'FL'}/results`, {
            state: { searchStateSnapshot: { ...searchState, ...newState } }
        });
    };

    const handlePopularSearch = (searchItem) => {
        // Validate plan selection before allowing search
        if (!validatePlanSelection()) {
            return; // Stop search if validation fails
        }

        // Get coordinates from context (app memory) — currentStateLocation, not the raw
        // context value, so a stale cross-state cached location can never be searched against.
        let lat = null;
        let lon = null;
        if (currentStateLocation) {
            lat = currentStateLocation.latitude;
            lon = currentStateLocation.longitude;
        }

        // searchItem is an object with displayName and value (psId) properties
        const newState = {
            query: '',
            superCategory: '',
            subCategory: '',
            popularSearchId: searchItem.value,
            psLabel: searchItem.displayName,
            selectedPlan,
            latitude: lat,
            longitude: lon,
            filters: { ...DEFAULT_FILTERS, distance },
            currentPage: 1
        };
        updateSearchState(newState);

        navigate(`/find-care/${resolvedEnvPlan || 'FL'}/results`, {
            state: { searchStateSnapshot: { ...searchState, ...newState } }
        });
    };

    const handleBrowseBySearch = (categoryData) => {
        // Validate plan selection before allowing search
        if (!validatePlanSelection()) {
            return; // Stop search if validation fails
        }

        // Get coordinates from context (app memory) — currentStateLocation, not the raw
        // context value, so a stale cross-state cached location can never be searched against.
        let lat = null;
        let lon = null;
        if (currentStateLocation) {
            lat = currentStateLocation.latitude;
            lon = currentStateLocation.longitude;
        }

        // Check if this is a subcategory drill-down (object with both values) or regular category (string)
        let superCategory, subCategory;
        if (typeof categoryData === 'object' && categoryData.superCategory && categoryData.subCategory) {
            // Subcategory drill-down - use both values
            superCategory = categoryData.superCategory;
            subCategory = categoryData.subCategory;
        } else {
            // Regular category search - just superCategory
            superCategory = categoryData;
            subCategory = null;
        }

        const newState = {
            query: '',
            superCategory,
            subCategory: subCategory || '',
            popularSearchId: '',
            psLabel: '',
            selectedPlan,
            latitude: lat,
            longitude: lon,
            filters: { ...DEFAULT_FILTERS, distance },
            currentPage: 1
        };
        updateSearchState(newState);

        navigate(`/find-care/${resolvedEnvPlan || 'FL'}/results`, {
            state: { searchStateSnapshot: { ...searchState, ...newState } }
        });
    };

    return (
        <main className="container-fluid">
            {/* Mandatory plan-selection modal — see showPlanSelectionModal's declaration above
                for exactly when this opens. Portaled to document.body (see planSelectionModal.jsx)
                so its placement here is purely for readability, not layout. */}
            <PlanSelectionModal
                isOpen={showPlanSelectionModal}
                plans={availablePlansForModal}
                stateName={getStateName()}
                onPlanSelect={handlePlanSelectFromModal}
            />

            {/* Find Care Title Section — swaps to "Find a CSED provider" while the CSED
                provider map view is showing (see showCsedProviderMap above). */}
            
            {showCsedProviderMap ? (
                <CSEDProviderMap
                    globalAssetsPath={resolvedEnvPlan}
                    visible={true}
                    onFindProvider={handleCsedFindProvider}
                />
            ) : (
              <>

<header className="find-care-wrapper">
                <h1 className="find-care-title">Find care</h1>
            </header>

            {translationError && (
                <ApiErrorBanner type={ERROR_TYPES.TRANSLATION} />
            )}

            <SearchLocation 
                onPlanChange={handlePlanChange}
                onDistanceChange={handleDistanceChange}
                showPlanError={showPlanError}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                locationAddress={locationAddress}
                onLocationChange={setLocationAddress}
                initialDistance={distance}
                envPlanCode={resolvedEnvPlan}
                autoOpenLocationPanelSignal={autoOpenLocationPanelSignal}
                autoOpenLocationPanelError={autoOpenLocationPanelError}
            />
           <SearchBar
               onSearch={handleSearch}
               placeholder="Enter provider name, specialty, or place of care..."
               validateBeforeSearch={validatePlanSelection}
               selectedPlan={selectedPlan}
               radiusMiles={distance}
               initialValue={initialQuery}
           />
           
           {/* Popular Search - conditionally rendered based on feature flag */}
           {isSectionEnabled('popularSearch') && (
               <PopularSearch onSearchClick={handlePopularSearch} />
           )}
           
           {/* Browse By - conditionally rendered based on feature flag */}
           {isSectionEnabled('browseBy') && (
               <BrowseBy onSearchClick={handleBrowseBySearch} />
           )}
           
           {/* State Options - conditionally rendered based on feature flag */}
           {isSectionEnabled('stateOptions') && (
               <StateOptions onCsedProviderClick={handleCsedProviderClick} />
           )}
           
           {/* Legal Notices Section - moved from footer */}
           <footer className="legal-notices-section">
               <h2 id="searchPageLegalNotice">Legal notices</h2>
               <div id="searchPageLegalNoticesText">
                  Health care providers taking part in our network are independent contractors. They aren't agents or employees of Aetna Better Health. We can't guarantee  any specific provider can see you. Our provider and pharmacy networks can change any time. We'll send you notice when needed. {planFeatures?.planName === 'MD' && ( <span>You'll want to call the provider before your visit. Ask if they're in your plan's network. This can help avoid extra costs</span>)} Contact us if you're looking for a specific provider. Or if the provider you're looking for isn't accepting new patients. We can help.

                   <br /><br />
                   We update our online provider list daily.
               </div>
               <a 
                   href={getFooterLinks()?.printDirectory || '#'} 
                   className="directory-link"
                   {...(getFooterLinks()?.printDirectory ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
               >
                   Learn how to get a directory in PDF or print format
                   <img src={arrowRight} alt="" className="directory-link-arrow" aria-hidden="true"/>
               </a>
               <div className="legal-notices-disclaimer" style={{ marginTop: '15px' }}>
                   {planFeatures?.cmsCode && <p style={{ fontSize: '14px' }}>{planFeatures.cmsCode}</p>}
                   {planFeatures?.updatedAt && (
                       <p style={{ fontSize: '14px' }}>Page updated: {new Date(planFeatures.updatedAt).toLocaleDateString('en-US')}</p>
                   )}
               </div>
           </footer>
              </>
            )}
        </main>
    );
};

export default Search;